import { saveTableRowsPerPage } from 'core/service/user-preferences/user-preferences.service';
import { RowsPerPage, RowsPerPageTarget } from 'core/service/user-preferences/domain/model/row-per-page.model';
import { DEFAULT_PAGINATION_SIZES } from 'core/constants/constants';
import { executeRequest } from 'core/utils/backend-service';
import { showErrorNotification } from 'core/utils/notification-service';
import { t } from 'core/i18n/i18n';
import { useCallback, useRef, useState } from 'react';

export const DEFAULT_PAGINATION_SIZES_BY_TARGET: RowsPerPage = {
    operators: DEFAULT_PAGINATION_SIZES[0],
    devices: DEFAULT_PAGINATION_SIZES[0],
    trainings: DEFAULT_PAGINATION_SIZES[0],
};

export const useUserPageSizerManager = ({ userPreferencesId }: { userPreferencesId: string }) => {
    const [pageSize, setPageSize] = useState<RowsPerPage>(DEFAULT_PAGINATION_SIZES_BY_TARGET);
    const [isLoading, setIsLoading] = useState(false);
    const pageSizeRef = useRef(pageSize);
    pageSizeRef.current = pageSize;

    const executeSavePageSize = async (
        previousPageSize: RowsPerPage,
        newPageSize: number,
        target: RowsPerPageTarget
    ) => {
        try {
            await executeRequest(() => saveTableRowsPerPage(userPreferencesId, newPageSize, target));
        } catch {
            showErrorNotification(t('notification.error.save-page-size'));
            setPageSize(() => ({ ...previousPageSize }));
        } finally {
            setIsLoading(false);
        }
    };

    const updatePageSize = useCallback(
        (newPageSize: number, target: RowsPerPageTarget) => {
            const key = target.toLowerCase() as keyof RowsPerPage;

            if (newPageSize !== pageSizeRef.current[key]) {
                const previousPageSize = pageSizeRef.current;
                setIsLoading(true);
                setPageSize((prev) => ({ ...prev, [key]: newPageSize }));
                return executeSavePageSize(previousPageSize, newPageSize, target);
            }
        },
        [userPreferencesId]
    );

    return {
        pageSize,
        isPaginationLoading: isLoading,
        setPageSize,
        updatePageSize,
    };
};
